import * as THREE from 'three'
import { useRef, useMemo, useEffect, useReducer } from 'react' // Added useReducer
import { Canvas, useFrame } from '@react-three/fiber'
import { Environment, Lightformer, useGLTF } from '@react-three/drei' // Added useGLTF
import { BallCollider, Physics, RigidBody } from '@react-three/rapier' // Using @react-three/rapier now
import { easing } from 'maath'
import { Effects } from './Effects' // Assuming Effects.jsx exists

// IMPORTANT: Replace this with the actual path to your GLB model.
// Example: If your model is in the 'public' folder, use "/my_model.glb".
// If it's in 'public/assets/models/', use "/assets/models/my_custom_model.glb".
const GLB_MODEL_PATH = "/heart.glb"; // <<< VERIFY THIS PATH!

const accents = ['#ff4060', '#ffcc00', '#20ffa0', '#4060ff']
const shuffle = (accent = 0) => [
  { color: '#444', roughness: 0.1, metalness: 0.5 },
  { color: '#444', roughness: 0.1, metalness: 0.5 },
  { color: '#444', roughness: 0.1, metalness: 0.5 },
  { color: 'white', roughness: 0.1, metalness: 0.1 },
  { color: 'white', roughness: 0.1, metalness: 0.1 },
  { color: 'white', roughness: 0.1, metalness: 0.1 },
  { color: accents[accent], roughness: 0.1, accent: true },
  { color: accents[accent], roughness: 0.1, accent: true },
  { color: accents[accent], roughness: 0.1, accent: true },
  { color: '#444', roughness: 0.1 },
  { color: '#444', roughness: 0.3 },
  { color: '#444', roughness: 0.3 },
  { color: 'white', roughness: 0.1 },
  { color: 'white', roughness: 0.2 },
  { color: 'white', roughness: 0.1 },
  { color: accents[accent], roughness: 0.1, accent: true, transparent: true, opacity: 0.5 },
  { color: accents[accent], roughness: 0.3, accent: true },
  { color: accents[accent], roughness: 0.1, accent: true }
]

export default function Hearts(props) {
  const [accent, click] = useReducer((state) => ++state % accents.length, 0)
  const connectors = useMemo(() => shuffle(accent), [accent])
  return (
    <div className='w-screen h-screen'>
    <Canvas flat shadows onClick={click} dpr={[1, 1.5]} gl={{ antialias: false }} camera={{ position: [0, 0, 30], fov: 17.5, near: 10, far: 40 }} {...props}>
      <color attach="background" args={['#141622']} />
      <Physics /*debug*/ timeStep="vary" gravity={[0, 0, 0]}>
        <Pointer />
        {connectors.map((props, i) => (
          <Sphere key={i} {...props} />
        ))}
      </Physics>
      <Environment resolution={256}>
        <group rotation={[-Math.PI / 3, 0, 1]}>
          <Lightformer form="circle" intensity={100} rotation-x={Math.PI / 2} position={[0, 5, -9]} scale={2} />
          <Lightformer form="circle" intensity={2} rotation-y={Math.PI / 2} position={[-5, 1, -1]} scale={2} />
          <Lightformer form="circle" intensity={2} rotation-y={Math.PI / 2} position={[-5, -1, -1]} scale={2} />
          <Lightformer form="circle" intensity={2} rotation-y={-Math.PI / 2} position={[10, 1, 0]} scale={8} />
          <Lightformer form="ring" color="#4060ff" intensity={80} onUpdate={(self) => self.lookAt(0, 0, 0)} position={[10, 10, 0]} scale={10} />
        </group>
      </Environment>
      <Effects />
    </Canvas></div>
  )
}

function Sphere({ position, children, vec = new THREE.Vector3(), scale = 1, r = THREE.MathUtils.randFloatSpread, accent, color = 'white', ...props }) {
  const api = useRef(); // Ref for the physics API
  const meshGroupRef = useRef(); // Ref for the group that will hold the cloned GLB scene
  const materialToDampenRef = useRef(); // Ref to hold the material of the GLB mesh for color damping

  // Load the GLB model. useGLTF will suspend until the model is loaded.
  const { scene } = useGLTF(GLB_MODEL_PATH);

  // Initial position for the physics body
  const pos = useMemo(() => position || [r(10), r(10), r(10)], [position, r]);

  // Calculate collider arguments dynamically based on the GLB model's bounding sphere
  const colliderRadius = useMemo(() => {
    let radius = 1; // Default fallback radius

    if (scene) {
        let primaryMeshInScene = null;
        // Traverse the original scene to find the first mesh
        scene.traverse((child) => {
            if (child.isMesh) {
                primaryMeshInScene = child;
                // Ensure bounding sphere is computed for accurate radius
                if (!child.geometry.boundingSphere) {
                    child.geometry.computeBoundingSphere();
                }
                return; // Found the first mesh
            }
        });

        if (primaryMeshInScene && primaryMeshInScene.geometry.boundingSphere) {
            radius = primaryMeshInScene.geometry.boundingSphere.radius;
            console.log("Calculated collider radius:", radius);
        } else {
            console.warn("No mesh found in GLB scene to compute bounding sphere for collider. Using default radius.");
        }
    }
    return radius;
  }, [scene]); // Recalculate if the GLB scene object changes

  // Effect to clone the scene, create new material, and apply it
  useEffect(() => {
    if (scene && meshGroupRef.current) {
      // Clone the scene for this specific instance to avoid modifying the original GLB data
      const clonedScene = scene.clone();
      
      // Create a new MeshStandardMaterial to apply the desired props
      const newMaterial = new THREE.MeshStandardMaterial({
        color: new THREE.Color(color), // Set initial color
        roughness: props.roughness || 0.1, // Use prop or default
        metalness: props.metalness || 0.1, // Use prop or default
        transparent: props.transparent || false, // Use prop or default
        opacity: props.opacity !== undefined ? props.opacity : 1, // Use prop or default
        // Add other material properties from props if needed (e.g., map, normalMap etc.)
      });

      // Traverse the cloned scene and apply the new material to all meshes
      clonedScene.traverse((child) => {
        if (child.isMesh) {
          child.material = newMaterial; // Override with the new material
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });

      materialToDampenRef.current = newMaterial; // Store the new material for color damping

      // Add the cloned scene to the group that is attached to the RigidBody
      meshGroupRef.current.add(clonedScene);

      // Clean up on unmount
      return () => {
        if (clonedScene.parent) {
          clonedScene.parent.remove(clonedScene);
        }
        // Dispose of the custom material to prevent memory leaks
        newMaterial.dispose(); 
      };
    }
  }, [scene, color, props.roughness, props.metalness, props.transparent, props.opacity]); // Re-run if these props change

  useFrame((state, delta) => {
    delta = Math.min(0.1, delta); // Clamp delta for stability
    
    // Apply impulse to drive towards center (original physics logic)
    api.current?.applyImpulse(vec.copy(api.current.translation()).negate().multiplyScalar(0.2));
    
    // Apply color damping to the GLB's material
    if (materialToDampenRef.current) {
      easing.dampC(materialToDampenRef.current.color, color, 0.2, delta);
    }
  });

  return (
    <RigidBody linearDamping={4} angularDamping={1} friction={0.1} position={pos} ref={api} colliders={false}>
      {/* Use BallCollider for the GLB model's physics boundary, with calculated radius */}
      <BallCollider args={[colliderRadius]} />
      
      {/* The GLB model itself is rendered inside this group */}
      {/* The ref is now on the group that holds the GLB model */}
      <group ref={meshGroupRef} scale={2} castShadow receiveShadow> 
        {children} {/* Render any children passed to Sphere (e.g., additional lights) */}
      </group>
    </RigidBody>
  );
}

function Pointer({ vec = new THREE.Vector3() }) {
  const ref = useRef()
  useFrame(({ mouse, viewport }) => ref.current?.setNextKinematicTranslation(vec.set((mouse.x * viewport.width) / 2, (mouse.y * viewport.height) / 2, 0)))
  return (
    <RigidBody position={[0, 0, 0]} type="kinematicPosition" colliders={false} ref={ref}>
      <BallCollider args={[1]} />
    </RigidBody>
  )
}
